import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import * as IconMDI from 'mdi-material-ui';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { cropImage } from './CropImage';
import { reportError } from '../../lib/errors';
import useStyles from './styles';
import { useSnackbar } from 'notistack';
import { Button } from '@elevator/ui';

// Constants
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

/**
 * ImageCropperDialog Component
 *
 * A full-featured image cropping dialog component that allows users to crop images
 * with interactive controls. Supports both File objects and string URLs as image sources.
 *
 * Features:
 * - Interactive cropping using react-easy-crop
 * - Zoom controls via slider (1x to 3x)
 * - Aspect ratio enforcement
 * - Mobile fullscreen support with AppBar header
 * - Desktop dialog mode with standard MUI Dialog
 * - Loading states during image cropping operation
 * - Automatic cleanup of object URLs for File blobs
 *
 * The component automatically handles URL creation and cleanup for File objects,
 * and accepts string URLs directly for external images.
 *
 * @param props - Component props
 * @param props.cropCandidate - The image to crop (File object or string URL). Can be null.
 * @param props.onImageCropped - Callback invoked when cropping is complete, receives the cropped File
 * @param props.aspect - Aspect ratio for the crop area (width/height). Must be > 0.
 * @param props.onClose - Handler for dialog close events
 * @param props.onImageCropCancel - Handler for cancel button click
 * @param props.open - Whether the dialog is open (from DialogProps)
 */
const ImageCropperDialog = ({
  cropCandidate,
  onImageCropped,
  aspect,

  // Modal Props
  onClose,
  onImageCropCancel,

  // Rest props are passed to <Dialog /> component
  ...rest
}: ImageCropperDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { classes } = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  // Create URL from file or accept direct string URL; cleanup only for File blobs
  const cropCandidateUrl = useMemo(() => {
    if (!cropCandidate) return '';
    return typeof cropCandidate === 'string'
      ? cropCandidate
      : URL.createObjectURL(cropCandidate);
  }, [cropCandidate]);

  // Cleanup object URLs when component unmounts or cropCandidate changes
  useEffect(() => {
    return () => {
      if (cropCandidate && typeof cropCandidate !== 'string') {
        try {
          URL.revokeObjectURL(cropCandidateUrl);
        } catch {
          // Ignore errors when revoking object URL
        }
      }
    };
  }, [cropCandidate, cropCandidateUrl]);

  // Cropper state management
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  /**
   * Called every time a zoom percentage is changed
   */
  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  /**
   * Called every single time crop area is moved
   */
  const onCropChange = useCallback((crop: Point) => {
    setCrop(crop);
  }, []);

  /**
   * Called when the user lets go of the view box which signifies a complete interaction
   */
  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  /**
   * Shows an error snackbar notification
   */
  const showErrorSnackbar = useCallback(
    (msg: string) => {
      enqueueSnackbar(msg, {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        variant: 'error',
        preventDuplicate: true,
      });
    },
    [enqueueSnackbar],
  );

  /**
   * Callback for when the user clicks 'Done'. This will crop the image and call parent function with cropped image
   */
  const onFinishClick = useCallback(async () => {
    if (!croppedAreaPixels || !cropCandidate) {
      showErrorSnackbar('Please select a crop area');
      return;
    }

    setIsCropping(true);
    try {
      // Ensure cropCandidate is a File for cropImage function
      if (typeof cropCandidate === 'string') {
        showErrorSnackbar('Cannot crop external URLs. Please upload a file.');
        setIsCropping(false);
        return;
      }

      const croppedImageResult = await cropImage(
        cropCandidateUrl,
        croppedAreaPixels,
        cropCandidate,
      );

      // Pass result to parent callback
      onImageCropped(croppedImageResult);
      onClose();
    } catch (err) {
      reportError(err, {
        metaData: {
          operation: 'user tried to crop image',
        },
      });
      showErrorSnackbar('An error occurred while cropping your image');
    } finally {
      setIsCropping(false);
    }
  }, [
    croppedAreaPixels,
    cropCandidate,
    cropCandidateUrl,
    onImageCropped,
    onClose,
    showErrorSnackbar,
  ]);

  const renderDialogHeader = () => {
    return (
      <AppBar elevation={0} position={'fixed'} className={classes.appbar}>
        <div className={classes.appBarToolbar}>
          <IconButton onClick={onClose} color="inherit" aria-label="close" size="large">
            <IconMDI.Close />
          </IconButton>
          <Typography variant="h6">Crop Image</Typography>
        </div>
      </AppBar>
    );
  };

  const renderImageCropArea = () => {
    const effectiveAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
    return (
      <Box paddingTop={2}>
        {/* react-easy-crop requires the direct container to have explicit width & height */}
        <Box
          position={'relative'}
          height={mobile ? '60vh' : '350px'}
          width={'100%'}
          style={{ touchAction: 'none' }}
        >
          {cropCandidateUrl ? (
            <Cropper
              image={cropCandidateUrl}
              crop={crop}
              zoom={zoom}
              onCropChange={onCropChange}
              onCropComplete={onCropComplete}
              onZoomChange={onZoomChange}
              onMediaLoaded={() => {
                // Reset zoom and crop position when new image loads
                setZoom(1);
                setCrop({ x: 0, y: 0 });
              }}
              restrictPosition={false}
              aspect={effectiveAspect}
              minZoom={MIN_ZOOM}
              zoomWithScroll
              objectFit={'contain'}
              mediaProps={{ crossOrigin: 'anonymous' }}
            />
          ) : (
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              height={'100%'}
              width={'100%'}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderZoomSlider = () => {
    return (
      <Box
        paddingY={1}
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
      >
        <Slider
          color={'secondary'}
          aria-labelledby="discrete-zoom-slider"
          valueLabelDisplay="auto"
          step={0.1}
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          onChange={(_, value) => onZoomChange(value as number)}
          value={zoom}
        />
        <Box paddingLeft={1}>
          <Typography variant={'caption'} id="discrete-zoom-slider" gutterBottom>
            Zoom
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      aria-labelledby="dialog-title"
      data-component="ImageCropperDialog"
      slotProps={{
        paper: {
          classes: { root: classes.paperStyle },
        },
      }}
      fullScreen={mobile}
      disableEscapeKeyDown
      {...rest}
    >
      {!mobile && <DialogTitle id={'scroll-dialog-title'}>Crop Image</DialogTitle>}
      {mobile && renderDialogHeader()}
      <DialogContent dividers>
        {isCropping ? (
          <Box
            minHeight={mobile ? '100%' : '350px'}
            width={'100%'}
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <CircularProgress color={'primary'} />
            <Box paddingTop={2}>
              <Typography variant={'body2'}>Cropping Image</Typography>
            </Box>
          </Box>
        ) : (
          <>
            {renderImageCropArea()}
            {renderZoomSlider()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Box display={'flex'} justifyContent={'space-between'} flexDirection={'row'}>
          <Button onClick={onImageCropCancel} variant={'text'} fullWidth>
            Cancel
          </Button>
          <Box paddingX={1} />
          <Button
            onClick={onFinishClick}
            variant={'text'}
            fullWidth
            disabled={isCropping}
          >
            Done
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export interface ImageCropperDialogProps extends DialogProps {
  /** The image to crop (File object or string URL). Can be null, but component won't render. */
  cropCandidate: File | string | null;
  /** Callback invoked when cropping is complete, receives the cropped File */
  onImageCropped: (file: File) => void;
  /** Aspect ratio for the crop area (width/height). Must be > 0. Defaults to 1 if invalid. */
  aspect: number;
  /** Handler for dialog close events */
  onClose: (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void;
  /** Handler for cancel button click */
  onImageCropCancel: () => void;
}

export default ImageCropperDialog;
